import React, { useState, useEffect, useMemo } from 'react'
import { useConfig } from '@peracto/peracto-config'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxFull } from '@fortawesome/pro-regular-svg-icons/faBoxFull'
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight'
import algoliasearch from 'algoliasearch/lite'
import { InstantSearch, connectHits, Configure, connectStats } from 'react-instantsearch-dom'

import isEmpty from 'lodash/isEmpty'
import cn from 'classnames'
import InnerHtml from '@peracto/peracto-editor-ui/dist/InnerHtml'

const useWindowSize = () => {
    const [windowWidth, setWindowWidth] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth
        }
        return 0
    })

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth)
        }

        if (typeof window === 'undefined') {
            return
        }

        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return windowWidth
}

let Icon = () => (
    <div className="flex w-full items-center justify-center py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
)

const ProductCard = ({ product, isEditMode }) => {
    const name = product?.attributes?.product_name

    const image = product?.resources?.find((resource) => resource?.type === 'image')?.location
    const productHref = `/product/${product.slug}`
    const rawLeadTime = product.attributes.lead_time
    const leadTimeText = rawLeadTime && rawLeadTime !== '0' ? `${rawLeadTime} Days` : null

    return (
        <div data-testid="product-card" className="flex h-full w-full flex-col justify-between">
            <div>
                <a
                    data-testid="product-card__link"
                    href={productHref}
                    className="mb-[10px] flex flex-col gap-y-[10px]"
                    onClick={(e) => {
                        if (isEditMode) {
                            e.preventDefault()
                        }
                    }}
                >
                    <div className="relative">
                        <img
                            className="aspect-square w-full rounded border border-brand-grey object-contain"
                            data-testid="product-card__image"
                            src={image || '/images/placeholder.png'}
                            alt={name}
                            width={310}
                            height={310}
                        />
                    </div>

                    <h5 data-testid="product-card__name" className={cn('block')}>
                        {name}
                    </h5>
                </a>
            </div>

            <div>
                <div className="mb-[10px] flex flex-col gap-y-[10px]">
                    <span
                        data-testid="product-card__in-stock"
                        className="flex items-center gap-x-[10px] text-sm font-bold"
                    >
                        {leadTimeText || 'In stock'}
                    </span>
                </div>
            </div>
        </div>
    )
}

const Hits = (props) => {
    const {
        hits,
        isEditMode,
        fallbackImage,
        border,
        numberOfProducts,
        currentPage,
        PRODUCTS_PER_PAGE,
    } = props

    let productsToRender = hits

    if (numberOfProducts !== undefined && numberOfProducts > 0) {
        const productsAlreadyRendered = currentPage * PRODUCTS_PER_PAGE

        const productsRemaining = numberOfProducts - productsAlreadyRendered

        if (productsRemaining < hits.length) {
            productsToRender = hits.slice(0, productsRemaining)
        }
    }

    const inStorefront = !window?.location?.pathname.includes('/content/edit/')

    return productsToRender.length > 0 ? (
        <>
            {productsToRender.map((hit, index) => (
                <ProductCard
                    key={hit?.slug || `hit_${index}`}
                    product={hit}
                    isEditMode={isEditMode}
                    fallbackImage={fallbackImage}
                    border={border}
                    index={index}
                />
            ))}
        </>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null
}
const CustomHits = connectHits(Hits)

const StatsUpdater = ({ nbHits, onStatsUpdate }) => {
    useEffect(() => {
        if (onStatsUpdate) {
            onStatsUpdate({ nbHits })
        }
    }, [nbHits, onStatsUpdate])

    return null
}
const ConnectedStatsUpdater = connectStats(StatsUpdater)

const ProductsContent = ({
    numberOfProducts,
    attributes = [],
    attributeOperator = 'and',
    border,
    isEditMode,
    currentPage,
    PRODUCTS_PER_PAGE,
    onStatsUpdate,
}) => {
    const config = useConfig()
    const algolia = config.get('algolia') || {}

    const { applicationId, searchApiKey, indexName } = algolia

    const searchClient = algoliasearch(applicationId, searchApiKey)

    const [facetFilters, setFacetFilters] = useState()

    useEffect(() => {
        if (attributes?.length > 0) {
            const categories = attributes?.reduce((acc, { attribute, value }) => {
                // Attribute is already saved with prefix (e.g. 'attributes.brand' or 'sku')
                // so we can use it directly
                if (value.length > 0) acc.push(`${attribute}:${value}`)

                return acc
            }, [])

            setFacetFilters(categories)
        }
    }, [attributes])

    if (isEmpty(algolia)) {
        console.error(
            'No Algolia configuration defined. Please refer to the Peracto documentation.'
        )
    }

    return useMemo(
        () =>
            isEmpty(algolia) ? null : (
                <InstantSearch searchClient={searchClient} indexName={indexName}>
                    <Configure
                        hitsPerPage={PRODUCTS_PER_PAGE}
                        page={currentPage}
                        facetFilters={
                            facetFilters
                                ? attributeOperator === 'and'
                                    ? facetFilters
                                    : [facetFilters]
                                : null
                        }
                    />

                    <ConnectedStatsUpdater onStatsUpdate={onStatsUpdate} />

                    <CustomHits
                        isEditMode={isEditMode}
                        border={border}
                        numberOfProducts={numberOfProducts}
                        currentPage={currentPage}
                        PRODUCTS_PER_PAGE={PRODUCTS_PER_PAGE}
                    />
                </InstantSearch>
            ),
        [
            algolia,
            searchClient,
            indexName,
            border,
            facetFilters,
            attributeOperator,
            isEditMode,
            PRODUCTS_PER_PAGE,
            currentPage,
            onStatsUpdate,
            numberOfProducts,
        ]
    )
}

const ProductsRenderer = ({ state, ...props }) => {
    const { title = 'Products', description = '', numberOfProducts, viewAllLink = '/' } = state

    const windowWidth = useWindowSize()

    const TABLET_BREAKPOINT = 768
    const DESKTOP_BREAKPOINT = 992

    const PRODUCTS_PER_PAGE = useMemo(() => {
        if (windowWidth === 0) {
            return 1
        }
        if (windowWidth < TABLET_BREAKPOINT) {
            return 1
        }
        if (windowWidth < DESKTOP_BREAKPOINT) {
            return 2
        }
        return 4
    }, [windowWidth])

    const [currentPage, setCurrentPage] = useState(0)
    const [totalPages, setTotalPages] = useState(1)

    const isAtStart = currentPage === 0
    const isAtEnd = currentPage + 1 >= totalPages

    const handlePageChange = (direction) => {
        if (direction === 'left' && !isAtStart) {
            setCurrentPage((prevPage) => prevPage - 1)
        } else if (direction === 'right' && !isAtEnd) {
            setCurrentPage((prevPage) => prevPage + 1)
        }
    }

    const handleStatsUpdate = ({ nbHits }) => {
        let effectiveHits = nbHits
        if (numberOfProducts !== undefined && nbHits > numberOfProducts) {
            effectiveHits = numberOfProducts
        }

        const newTotalPages = Math.ceil(effectiveHits / PRODUCTS_PER_PAGE)
        setTotalPages(newTotalPages > 0 ? newTotalPages : 1)
    }

    useEffect(() => {
        setCurrentPage(0)
    }, [PRODUCTS_PER_PAGE])

    return (
        <div className="pl-[15px]">
            <div className="mb-[15px] flex flex-col items-center justify-between md:flex-row">
                <h2
                    data-testid="product-relations__title"
                    className="!lg:text-[40px] !lg:leading-[46px] !mb-0 text-center !text-[30px] !leading-[34px] text-brand-black"
                >
                    {title}
                </h2>

                <div className={cn("hidden items-center gap-[10px] md:flex", description && "mb-[10px]")}>
                    <NavButton
                        direction="left"
                        disabled={isAtStart}
                        onClick={() => handlePageChange('left')}
                    />
                    <NavButton
                        direction="right"
                        disabled={isAtEnd}
                        onClick={() => handlePageChange('right')}
                    />
                </div>
            </div>
            {description && (
                <InnerHtml content={description} className="mt-[-10px] md:mt-[-20px]" />
            )}

            <div className="product-card-container content__products flex flex-row flex-wrap justify-center gap-[20px] md:justify-start">
                <div className="grid grid-cols-1 gap-[15px] md:grid-cols-2 lg:grid-cols-4 lg:gap-[30px]">
                    <ProductsContent
                        {...state}
                        {...props}
                        currentPage={currentPage}
                        PRODUCTS_PER_PAGE={PRODUCTS_PER_PAGE}
                        onStatsUpdate={handleStatsUpdate}
                    />
                </div>
            </div>

            <div className="mt-[30px] flex items-center justify-start">
                <a
                    href={viewAllLink}
                    className="text-[24px] font-bold text-brand-secondary underline hover:text-brand-secondary-darken"
                >
                    View all products
                </a>
            </div>

            <div className="mt-[15px] flex items-center justify-center gap-[10px] md:hidden">
                <NavButton
                    direction="left"
                    disabled={isAtStart}
                    onClick={() => handlePageChange('left')}
                />
                <NavButton
                    direction="right"
                    disabled={isAtEnd}
                    onClick={() => handlePageChange('right')}
                />
            </div>
        </div>
    )
}

const NavButton = ({ direction, disabled, onClick }) => (
    <button
        className={cn(
            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
            { 'cursor-not-allowed opacity-25': disabled }
        )}
        onClick={onClick}
        disabled={disabled}
    >
        <FontAwesomeIcon icon={direction === 'left' ? faChevronLeft : faChevronRight} />
    </button>
)

export default ProductsRenderer
